import * as React from 'react';
import { AxiosError } from 'axios';

import { useApi, IApiConfig } from '../../hooks/useApi';
import {
    FeatureId,
    IFeedbackResponse,
    ISubmitFeedbackRequest,
} from './FeedbackWidget.types';

export type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

/**
 * Why a submit failed, as far as it changes what the user should do next:
 * - `network`: nothing came back, so it is worth checking the connection
 * - `session`: the token was refused, so retrying will not help until they sign in
 * - `rejected`: the API refused this request, so retrying as-is will not help
 * - `server`: the service failed or is throttling, so trying again later may work
 */
export type SubmitErrorKind = 'network' | 'session' | 'rejected' | 'server';

export interface IUseSubmitFeedback {
    submit: (rating: number, comment: string) => void;
    status: SubmitStatus;
    /** Set only while `status` is `error`. */
    errorKind?: SubmitErrorKind;
    reset: () => void;
}

const classifyError = (error: AxiosError): SubmitErrorKind => {
    const status = error.response?.status;
    if (status === undefined) return 'network';
    if (status === 401) return 'session';
    // 429 is a 4xx, but unlike the others it clears by itself.
    if (status === 429 || status >= 500) return 'server';
    return 'rejected';
};

/** Submits one piece of feedback to the user feedback service. */
export const useSubmitFeedback = (
    feedbackApi: string,
    tenantId: string,
    featureId: FeatureId,
): IUseSubmitFeedback => {
    const apiConfig = React.useMemo<IApiConfig>(
        () => ({
            url: encodeURI(`${feedbackApi}/v1/tenants/${tenantId}/feedback`),
            method: 'POST',
            useAuth: true,
            executeType: 'manual',
            useCache: false,
        }),
        [feedbackApi, tenantId],
    );

    const {
        data,
        loading,
        error,
        execute,
        reset: resetApi,
    } = useApi<IFeedbackResponse>(apiConfig);

    // useApi keeps the last response, so a submit is tracked separately.
    const [requested, setRequested] = React.useState(false);

    const submit = React.useCallback(
        (rating: number, comment: string) => {
            const trimmed = comment.trim();
            const body: ISubmitFeedbackRequest = {
                featureId,
                rating,
                ...(trimmed ? { comment: trimmed } : {}),
            };

            setRequested(true);
            execute({ data: body });
        },
        [execute, featureId],
    );

    // In a ref, as useApi rebuilds `reset` on every render.
    const resetApiRef = React.useRef(resetApi);
    resetApiRef.current = resetApi;

    const reset = React.useCallback(() => {
        setRequested(false);
        resetApiRef.current();
    }, []);

    const status = React.useMemo<SubmitStatus>(() => {
        if (!requested) return 'idle';
        if (loading) return 'submitting';
        if (error) return 'error';
        if (data) return 'success';
        return 'submitting';
    }, [requested, loading, error, data]);

    return {
        submit,
        status,
        errorKind:
            status === 'error' && error ? classifyError(error) : undefined,
        reset,
    };
};
